import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { TooltipPlacement } from './placement';
import { TooltipComponent } from './tooltip.component';

let nextId = 0;

@Directive({
    selector: '[appTooltip]',
    exportAs: 'appTooltip',
    standalone: false
})
export class TooltipDirective implements OnDestroy, OnChanges {

  private destroy$ = new Subject<void>();
  private tooltipWindowId = `app-tooltip-${nextId++}`;
  private overlayRef: OverlayRef;
  private tooltipRef: ComponentRef<TooltipComponent> | null;
  private boundKeyDownFunc = this.onKeyDown.bind(this);
  private boundTooltipMouseEnter = this.onTooltipMouseEnter.bind(this);
  private boundTooltipMouseLeave = this.onTooltipMouseLeave.bind(this);
  private tooltipMessage: string;
  private isTap = false;
  private observer: MutationObserver;
  private isTooltipHovered = false;
  private resizeObserver: ResizeObserver;
  private layoutObserver: MutationObserver;

  @Input() appTooltip = '';
  @Input() placement: TooltipPlacement = 'top';
  @Input() disableDefaultActions = false;
  @Input() disableTooltip = false;
  @Input() panelClass = '';
  @Input() alternateAriaLabel = '';
  @Input() isAriaLabelRequired = true;
  @Input() parent: HTMLElement | null = null;
  @Output() shown = new EventEmitter();
  @Output() hidden = new EventEmitter();
  @Output() toggleShown = new EventEmitter<string>();
  private static currentOpenTooltip: TooltipDirective | null = null;

  @HostListener('touchstart') onTouch() {
    this.isTap = true;
  }

  @HostListener('touchend')
  @HostListener('touchcancel')
  onTouchEnd() {
    this.isTap = false;
  }

  @HostListener('focusin') onFocus() {
    this.toggle();
  }

  @HostListener('focusout') onBlur() {
    this.close();
  }

  @HostListener('mouseenter', ['$event.target']) onMouseover(target: any) {
    if (target && target.disabled) {
      return;
    }
    this.open();
  }

  @HostListener('mouseleave', ['$event']) onmouseout(event: MouseEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    
    // Check if mouse is moving to the tooltip
    if (relatedTarget && this.isTooltipElement(relatedTarget)) {
      return; // Don't close if moving to tooltip
    }
    
    // Close the tooltip only if it is not hovered
    if (!this.isTooltipHovered) {
      this.close(); 
    }
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    this.observer = this.createDisabledMutationObserver();
    this.setupLayoutObservers();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onPlacementChange(changes.placement);
    this.onMessageChange(changes.appTooltip);
    if (this.isAriaLabelRequired) this.setAriaLabel();
  }

  ngOnDestroy() {
    this.close();
    this.observer.disconnect();
    this.cleanup();

    this.destroy$.next();
    this.destroy$.complete();
  }

  close() {
    if (this.tooltipRef) {
      const tooltipEl = this.tooltipRef.instance.el.nativeElement;
      tooltipEl.removeEventListener('mouseenter', this.boundTooltipMouseEnter);
      tooltipEl.removeEventListener('mouseleave', this.boundTooltipMouseLeave);

      this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
      this.tooltipRef.destroy();
      this.overlayRef.dispose();
      this.tooltipRef = null;

      window.removeEventListener('keydown', this.boundKeyDownFunc);
      this.cleanup();
      this.hidden.emit('TooltipDirective');
    }
  }

  isOpen(): boolean {
    return this.tooltipRef != null;
  }

  open() {
    /*
     * Ensure only one tooltip is open at any time.
     * If another tooltip is currently open (and it's not this one), close it first to avoid stuck open states during rapid hovering.
     */
    if (TooltipDirective.currentOpenTooltip && TooltipDirective.currentOpenTooltip !== this) {
      TooltipDirective.currentOpenTooltip.close();
    }
    TooltipDirective.currentOpenTooltip = this;

    if (this.isOpen() || this.disableTooltip || this.appTooltip.length === 0 || this.isTap) {
      return;
    }

    this.createTooltip();

    window.addEventListener('keydown', this.boundKeyDownFunc);
    this.shown.emit('TooltipDirective');
    this.toggleShown.emit('TooltipDirective');
  }

  toggle() {
    if (this.tooltipRef) {
      this.close();
    } else {
      this.open();
    }
  }

  private createTooltip() {
    const overlayConfig = this.getOverlayConfig();
    this.overlayRef = this.overlay.create(overlayConfig);

    const containerPortal = new ComponentPortal(TooltipComponent);
    const componentRef = this.overlayRef.attach(containerPortal);

    const hostEl = this.elementRef;
    const tooltipEl = componentRef.instance.el.nativeElement;

    tooltipEl.addEventListener('mouseenter', this.boundTooltipMouseEnter);
    tooltipEl.addEventListener('mouseleave', this.boundTooltipMouseLeave);

    componentRef.instance.id = this.tooltipWindowId;
    componentRef.instance.placement = this.placement;
    componentRef.instance.close = () => {
      this.close();
    };
    componentRef.instance.afterViewInit.subscribe(() => {
      // Tooltip placement is initially checked by Angular during view init.
      // setTimeout is used to defer the placement update until after the initial check.
      // This prevents ExpressionChangedAfterItHasBeenCheckedError.
      setTimeout(() => {
        // Tooltip may already be closed when this deferred callback runs.
        if (!this.tooltipRef || this.tooltipRef !== componentRef || !this.overlayRef) {
          return;
        }
        const updatedPlacement = this.getFlippedPlacement();
        this.tooltipRef.instance.placement = updatedPlacement;
        this.updatePositionStrategy(updatedPlacement, hostEl, tooltipEl);
      });
    });

    this.setTooltipMessage(componentRef, this.appTooltip);

    this.updatePositionStrategy(this.placement, hostEl, tooltipEl);

    this.tooltipRef = componentRef;

    // Setup observers after tooltip is created
    this.setupTooltipObservers();
  }

  getOverlayConfig(): OverlayConfig {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: this.panelClass,
    });

    return overlayConfig;
  }

  updatePositionStrategy(placement: TooltipPlacement, hostEl: ElementRef, tooltipEl: ElementRef) {
    const position = this.getPosition(placement, hostEl, tooltipEl);
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([position]);

    this.overlayRef.updatePositionStrategy(positionStrategy);
  }

  private setupLayoutObservers() {
    // Observe DOM changes that might change layout
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isOpen()) {
          this.repositionTooltipWithChangeDetection();
        }
      });
      this.resizeObserver.observe(document.body);
    }

  }

  private setupTooltipObservers() {
    if (!this.tooltipRef) return;
    // Also observe the element for position changes
    if (this.resizeObserver) {
      this.resizeObserver.observe(this.elementRef.nativeElement);
      // Observe parent containers that might affect positioning
      let parent = this.elementRef.nativeElement.parentElement;
      while (parent && parent !== document.body) {
        this.resizeObserver.observe(parent);
        parent = parent.parentElement;
      }
    }
  }

  private repositionTooltipWithChangeDetection() {
    if (this.tooltipRef && this.overlayRef) {

      // Trigger change detection first
      this.cdr.detectChanges();

      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        this.repositionTooltip();
      });
    }
  }

  private repositionTooltip() {
    if (this.tooltipRef && this.overlayRef) {
      const hostEl = this.elementRef;
      const tooltipEl = this.tooltipRef.instance.el.nativeElement;
      const updatedPlacement = this.getFlippedPlacement();
      this.tooltipRef.instance.placement = updatedPlacement;
      this.updatePositionStrategy(updatedPlacement, hostEl, tooltipEl);
    }
  }

  private cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.layoutObserver) {
      this.layoutObserver.disconnect();
    }
  }

  private getPosition(
    placement: TooltipPlacement,
    hostEl: ElementRef,
    tooltipEl: ElementRef
  ): ConnectedPosition {
    if (placement === 'top') {
      return {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -11,
      };
    } else if (placement === 'bottom') {
      return {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
      };
    } else if (placement === 'left') {
      const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
      const tooltipHeight = Number(tooltipEl.nativeElement.scrollHeight);

      return {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: -10,
        offsetY: Math.round((hostElHeight + tooltipHeight) / 2),
      };
    } else if (placement === 'right') {
      const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
      const tooltipHeight = Number(tooltipEl.nativeElement.scrollHeight);

      return {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: Math.round((hostElHeight + tooltipHeight) / 2),
      };
    } else {
      // Top is default
      return {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -10,
      };
    }
  }

  private onPlacementChange(change: SimpleChange) {
    if (this.tooltipRef && change) {
      this.tooltipRef.instance.placement = change.currentValue;
    }
  }

  private onMessageChange(change: SimpleChange) {
    if (this.tooltipRef && change) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.setTooltipMessage(this.tooltipRef, change.currentValue);
    }
  }

  private setTooltipMessage(
    componentRef: ComponentRef<TooltipComponent>,
    message: string | TemplateRef<any>
  ) {
    if (!componentRef) {
      return;
    }

    componentRef.instance.tooltipMessage = this.appTooltip;
    this.tooltipMessage = this.appTooltip;
  }

  /**
   * This is a workaround for FF to close tooltip when host button is programmatically disabled
   *  because `blur` event is not fired when element is disabled
   * Read more here: https://bugzilla.mozilla.org/show_bug.cgi?id=559561
   */
  private createDisabledMutationObserver(): MutationObserver {
    const observer = new MutationObserver((list) => {
      for (const record of list) {
        const target: any = record?.target;
        if (target?.disabled !== undefined) {
          const disabled = Boolean(target?.disabled);
          this.onDisabledChange(disabled);
        }
      }
    });

    observer.observe(this.elementRef.nativeElement, { attributeFilter: ['disabled'] });

    return observer;
  }

  private onDisabledChange(disabled: boolean) {
    if (disabled) {
      this.close();
    }
  }

  private onKeyDown(event: KeyboardEvent) {
    if (!this.disableDefaultActions && event.key === 'Escape') {
      this.close();
    }
  }

  private setAriaLabel() {
    let element: HTMLElement | Element | null;

    if (this.elementRef.nativeElement.tagName.toLowerCase() === 'app-button') {
      element = this.elementRef.nativeElement.firstElementChild;
    } else {
      element = this.elementRef.nativeElement;
    }

    this.renderer.setAttribute(element, 'aria-label', this.alternateAriaLabel || this.appTooltip);
  }

  private onTooltipMouseEnter() {
    this.isTooltipHovered = true;
  }

  private onTooltipMouseLeave() {
    this.isTooltipHovered = false;
    this.close();
  }

  /**
   * Check if the given element is part of the tooltip
   */
  private isTooltipElement(element: HTMLElement): boolean {
    if (!this.tooltipRef) {
      return false;
    }

    const tooltipElement = this.tooltipRef.instance.el.nativeElement;
    
    // Check if the element is the tooltip itself or a child of the tooltip
    return tooltipElement.contains(element) || tooltipElement === element;
  }
  
  /**
   * Get the flipped placement of the tooltip if it doesn't fit in the preferred placement
   * @returns The flipped placement
   */
  private getFlippedPlacement(): 'top' | 'bottom' | 'left' | 'right' {
    if (!this.tooltipRef) {
      return this.placement;
    }

    const boundary = this.getPlacementBoundaryRect();
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipRef.instance.el.nativeElement.getBoundingClientRect();

    const space = {
      top: rect.top - boundary.top,
      bottom: boundary.bottom - rect.bottom,
      left: rect.left - boundary.left,
      right: boundary.right - rect.right
    };
 
    const fits = {
      top: space.top >= tooltipRect.height + 10,
      bottom: space.bottom >= tooltipRect.height + 10,
      left: space.left >= tooltipRect.width + 10,
      right: space.right >= tooltipRect.width + 10
    };
 
    // If preferred placement fits then use the same
    if (fits[this.placement]) {
      return this.placement;
    }
  
    // Flip only to its opposite
    const opposite: Record<TooltipPlacement, TooltipPlacement> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left'
    };

    return opposite[this.placement];
  }

  /**
   * Get the boundary rectangle for the tooltip placement
   * @returns The boundary rectangle
   */
  private getPlacementBoundaryRect() {
    if (!this.parent) {
      return {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      };
    }

    return this.parent.getBoundingClientRect();
  }
}